import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import StatusChip from "../general/StatusChip";

export function formatAcquisitionListDate(iso?: string): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
}

function decisionColor(
  decision: string
): "success" | "error" | "warning" | "info" | "default" {
  if (decision === "SUCCESS") return "success";
  if (decision === "FAILED") return "error";
  if (decision === "IN_PROGRESS" || decision === "QUEUED") return "info";
  if (decision === "PENDING") return "default";
  return "default";
}

export function shouldShowDecisionChip(
  status?: string,
  decision?: string
): boolean {
  if (!decision) {
    return false;
  }
  const statusNorm = String(status || "").toUpperCase();
  const decisionNorm = decision.toUpperCase();
  if (!statusNorm || statusNorm === decisionNorm) {
    return false;
  }
  const statusBusy =
    statusNorm === "QUEUED" ||
    statusNorm === "IN_PROGRESS" ||
    statusNorm === "PENDING";
  const decisionBusy =
    decisionNorm === "QUEUED" ||
    decisionNorm === "IN_PROGRESS" ||
    decisionNorm === "PENDING";
  if (statusBusy && decisionBusy) {
    return false;
  }
  return true;
}

export const AcquisitionRecordRow: React.FC<{
  requestId: string;
  title?: string;
  summary: string;
  status?: string;
  decision?: string;
  createdAt: string;
  onClick: () => void;
}> = ({ requestId, title, summary, status, decision, createdAt, onClick }) => {
  const showDecision = shouldShowDecisionChip(status, decision);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        p: 1,
        borderBottom: "1px solid #ccc",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      }}
    >
      <Box sx={{ flex: "0 0 210px", whiteSpace: "nowrap" }}>
        <Typography variant="body1" noWrap>
          {requestId}
        </Typography>
      </Box>
      {title ? (
        <Box
          sx={{
            flex: "0 0 auto",
            maxWidth: 140,
            minWidth: 0,
            pr: 1,
          }}
        >
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ flex: "1 1 auto", minWidth: 0, pr: 1 }}>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {summary}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 0.5,
        }}
      >
        {showDecision ? (
          <Chip label={decision} color={decisionColor(decision!)} />
        ) : null}
        {status ? <StatusChip status={status} /> : null}
      </Box>
      <Box
        sx={{
          flex: "0 0 160px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Typography variant="body2">
          {formatAcquisitionListDate(createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};
